/**
 * The Song & Timing workspace panels (W2-08 timing; DESIGN.md §4.15): the shell lays out the slots
 * `timing.left | timing.main over timing.bottom` (A§6.2); this module fills them.
 *
 *  - `timing.left` (TimingLeftPanel): timing status (confirmed or not), Tempo (BPM, Detect, Tap), First beat,
 *    Tempo changes (with meter), Metronome and Song info, as collapsible sections.
 *  - `timing.main` (TimingMainPanel): the song overview (waveform + beat grid) over the bar-by-bar view, with the
 *    analysis status (progress, waiting for the song, errors with Retry).
 *  - `timing.bottom` (TimingBottomPanel): check by ear (Play with clicks, Too early / Just right / Too late) and the
 *    audio latency.
 * Each panel root carries `data-region="timing"` (focus atoms for the B / Shift+B / Alt+, / Alt+. bindings).
 * On first visit of a song the tempo is detected automatically (once per song and session; setting
 * `ext.timing.autoDetect`), and the project's edit policy is loaded.
 */

import { useCallback, useEffect, useRef } from 'react';
import type { PanelProps } from '../../app/registry/types';
import { Core } from '../../core/commands/index';
import type { AssetId } from '../../core/model/types';
import type { Gesture } from '../../state/types';
import { Button, EmptyState, Icon, ProgressBar, Section } from '../../ui';
import { AlignmentView } from './AlignmentView';
import { MetronomePanel } from './MetronomePanel';
import { OffsetHelper } from './OffsetHelper';
import { OffsetPanel } from './OffsetPanel';
import { SongInfoSection } from './SongInfoSection';
import { SongOverview } from './SongOverview';
import { TempoPanel } from './TempoPanel';
import { analysisErrorText } from './TempoDetect';
import { TempoTable } from './TempoTable';
import { m } from './timing.messages';
import { beatZeroAt } from './timingActions';
import { timingAnalysisFor, useSongAnalysis, type SongAnalysis } from './timingAnalysis';
import type { TimingView } from './timingDoc';
import { setConfirmed } from './timingEdits';
import {
  useMusic,
  useSettingsValue,
  useStore,
  useText,
  useTimingView,
  useTransport,
  type TimingText,
} from './timingHooks';
import { setOffsetArgFor } from './timingModel';
import { timingSessionFor } from './timingSession';
import s from './Timing.module.css';

function useAutoDetect(services: PanelProps['services'], audio: AssetId | null, analysis: SongAnalysis | null): void {
  const settings = useSettingsValue(services);
  const enabled = settings.ext['timing.autoDetect'] !== false;
  useEffect(() => {
    if (audio === null || analysis === null || !enabled) return;
    if (analysis.status !== 'ready' || analysis.candidates !== null || analysis.detecting) return;
    const session = timingSessionFor(services);
    if (session.get().autoDetected.has(audio)) return;
    session.set((st) => ({ autoDetected: new Set([...st.autoDetected, audio]) }));
    void timingAnalysisFor(services)
      .detectTempo(audio)
      .catch(() => undefined);
  }, [services, audio, analysis, enabled]);
}

function AnalysisStatus(props: {
  readonly services: PanelProps['services'];
  readonly text: TimingText;
  readonly analysis: SongAnalysis | null;
  readonly audio: AssetId;
}) {
  const { services, text, analysis, audio } = props;
  if (analysis === null || analysis.status === 'ready' || analysis.status === 'idle') return null;
  if (analysis.status === 'error') {
    return (
      <div className={s.status} data-kind="error" role="alert">
        <Icon name="CircleAlert" size={14} />
        <span className={s.grow}>{analysisErrorText(text, analysis.error ?? '')}</span>
        <Button
          size="sm"
          icon="RotateCcw"
          onClick={() => {
            timingAnalysisFor(services).retry(audio);
          }}
        >
          {text.t(m.retry)}
        </Button>
      </div>
    );
  }
  if (analysis.status === 'waitingForAudio') {
    return (
      <div className={s.status} role="status">
        <Icon name="Music" size={14} />
        <span className={s.grow}>{text.t(m.waitingForAudio)}</span>
        <Button
          size="sm"
          icon="Music"
          onClick={() => {
            void timingAnalysisFor(services)
              .loadSong(audio)
              .catch((err: unknown) => {
                services.ui.toast(
                  { key: m.analysisFailed, params: { error: err instanceof Error ? err.message : String(err) } },
                  { kind: 'error' },
                );
              });
          }}
        >
          {text.t(m.loadSong)}
        </Button>
      </div>
    );
  }
  return (
    <div className={s.status} role="status">
      <span>{text.t(m.analysingTask, { task: analysis.task ?? 'other' })}</span>
      <div className={s.grow}>
        <ProgressBar value={analysis.progress} label={{ key: m.analysing }} />
      </div>
    </div>
  );
}

/** Shared state of the three panels. */
function useTimingPanel(services: PanelProps['services']) {
  const text = useText(services);
  const store = useStore(services);
  const view = useTimingView(store);
  const audio = view?.music?.id ?? null;
  const analysis = useSongAnalysis(services, audio);
  useEffect(() => {
    if (audio !== null) timingAnalysisFor(services).ensure(audio);
  }, [services, audio]);
  return { text, store, view, audio, analysis };
}

function NoProject() {
  return (
    <div className={s.panel} data-region="timing">
      <EmptyState icon="AudioLines" title={{ key: m.noProject }} />
    </div>
  );
}

function NoSong() {
  return <EmptyState icon="Music" title={{ key: m.noSongTitle }} body={{ key: m.noSongBody }} />;
}

export function TimingLeftPanel(props: PanelProps) {
  const { services } = props;
  const { text, view, analysis, audio } = useTimingPanel(services);
  const music = useMusic(services);
  if (view === null) return <NoProject />;
  return (
    <div className={s.panel} data-region="timing" aria-label={text.t(m.panelLeft)} role="region">
      <ConfirmedStatus services={services} text={text} view={view} />
      <div className={s.scroll}>
        <Section title={text.t(m.sectionTempo)} collapsible defaultOpen>
          <TempoPanel services={services} text={text} view={view} analysis={analysis} />
        </Section>
        <Section title={text.t(m.sectionFirstBeat)} collapsible defaultOpen>
          <OffsetPanel services={services} text={text} view={view} analysis={analysis} />
        </Section>
        <Section title={text.t(m.sectionTempoChanges)} collapsible defaultOpen>
          <TempoTable services={services} text={text} view={view} />
        </Section>
        <Section title={text.t(m.sectionMetronome)} collapsible defaultOpen>
          <MetronomePanel services={services} text={text} />
        </Section>
        <Section title={text.t(m.sectionSongInfo)} collapsible defaultOpen={false}>
          {audio === null ? (
            <NoSong />
          ) : (
            <SongInfoSection
              services={services}
              text={text}
              view={view}
              music={music}
              analysedDurationSec={analysis?.durationSec ?? null}
            />
          )}
        </Section>
      </div>
    </div>
  );
}

function ConfirmedStatus(props: {
  readonly services: PanelProps['services'];
  readonly text: TimingText;
  readonly view: TimingView;
}) {
  const { services, text, view } = props;
  return (
    <div className={s.status} data-kind={view.confirmed ? 'ok' : 'warning'} data-testid="timing-confirmed">
      <Icon name={view.confirmed ? 'CircleCheck' : 'CircleAlert'} size={14} />
      <span className={s.grow}>
        {text.t(view.confirmed ? m.confirmedYes : m.confirmedNo)}
        {!view.confirmed && <span className={s.subtle}> {text.t(m.confirmedNoHelp)}</span>}
      </span>
      {view.confirmed ? (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => {
            setConfirmed(services, false);
          }}
        >
          {text.t(m.unconfirmAction)}
        </Button>
      ) : (
        <Button
          size="sm"
          variant="primary"
          icon="Check"
          onClick={() => {
            setConfirmed(services, true);
          }}
        >
          {text.t(m.confirmAction)}
        </Button>
      )}
    </div>
  );
}

export function TimingMainPanel(props: PanelProps) {
  const { services } = props;
  const { text, store, view, audio, analysis } = useTimingPanel(services);
  const transport = useTransport(services);
  const music = useMusic(services);
  useAutoDetect(services, audio, analysis);
  const gesture = useRef<{ readonly g: Gesture; readonly baseHeard: number } | null>(null);

  // Drag the grid: one live gesture, committed on release (Escape cancels).
  const onDrag = useCallback(
    (deltaSec: number, phase: 'move' | 'end' | 'cancel') => {
      const st = services.store;
      if (st === null) return;
      if (phase === 'cancel') {
        gesture.current?.g.cancel();
        gesture.current = null;
        return;
      }
      if (gesture.current === null) {
        const heard = st.doc.timing.offsetMs + st.doc.pkg.infoOffsetSec * 1000;
        gesture.current = {
          g: st.beginGesture({ label: { key: m.txOffset, params: { sec: heard / 1000 } }, command: 'timing.dragGrid' }),
          baseHeard: heard,
        };
      }
      const cur = gesture.current;
      const heard = Math.round(cur.baseHeard + deltaSec * 1000);
      const out = cur.g.update((tx) => {
        Core.tempo.setOffset(tx, { ms: setOffsetArgFor(heard, tx.doc.pkg) });
      });
      if (!out.ok) {
        cur.g.cancel();
        gesture.current = null;
        services.ui.toast(out.reason, { kind: 'error' });
        return;
      }
      if (phase === 'end') {
        cur.g.commit();
        gesture.current = null;
        services.ui.toast({ key: m.overviewDragged, params: { sec: heard / 1000 } }, { kind: 'info', undo: true });
      }
    },
    [services],
  );

  useEffect(
    () => () => {
      gesture.current?.g.cancel();
      gesture.current = null;
    },
    [],
  );

  if (view === null || store === null) return <NoProject />;
  if (audio === null) {
    return (
      <div className={s.panel} data-region="timing">
        <NoSong />
      </div>
    );
  }
  // The engine's decoded length is exact; the asset metadata next; the analysis is within one hop.
  const decoded = music !== null && music.asset === audio ? music.durationSec : null;
  const duration = decoded ?? view.music?.meta?.durationSec ?? analysis?.durationSec ?? 0;
  return (
    <div className={s.mainPanel} data-region="timing" aria-label={text.t(m.panelMain)} role="region">
      <div className={s.panel}>
        <AnalysisStatus services={services} text={text} analysis={analysis} audio={audio} />
        <SongOverview
          text={text}
          scheduler={services.scheduler}
          id="timing.overview"
          peaks={analysis?.peaks ?? null}
          onsets={analysis?.onsets ?? null}
          sampleRate={analysis?.sampleRate ?? null}
          durationSec={duration}
          tempo={view.tempo}
          meter={view.meterMap}
          shiftSec={view.shiftSec}
          playheadMusicSec={() => services.audio.transport.musicTime()}
          playing={transport.playing}
          onSeek={(sec) => {
            services.audio.transport.seek(sec - view.shiftSec);
          }}
          onBeatZero={(sec) => {
            beatZeroAt(services, sec);
          }}
          onDrag={onDrag}
          onHover={(sec) => {
            timingSessionFor(services).set({ overviewHoverSec: sec });
          }}
        />
      </div>
      <AlignmentView
        services={services}
        text={text}
        view={view}
        onsets={analysis?.onsets ?? null}
        durationSec={duration}
      />
    </div>
  );
}

export function TimingBottomPanel(props: PanelProps) {
  const { services } = props;
  const { text, view, audio } = useTimingPanel(services);
  if (view === null) return <NoProject />;
  return <OffsetHelper services={services} text={text} confirmed={view.confirmed} hasSong={audio !== null} />;
}
